import router from "@main/router";
import { resetStores as mainStoreReset } from "@main/stores";
import { message as AMessage } from "ant-design-vue";
import axios from "axios";
import type {
	AxiosInstance,
	AxiosRequestConfig,
	AxiosResponse,
	AxiosError,
	AxiosHeaders,
	AxiosRequestHeaders,
} from "axios";
import i18n from "@/application/i18n";
import { resetStores } from "@/stores";

interface HttpRequestConfigExtensions {
	/**
	 * 是否显示错误消息提示
	 */
	showMessage?: boolean;
	/**
	 * 是否显示全局loading
	 * @TODO: 未实现
	 */
	showLoading?: boolean;
	/**
	 * 是否返回blob数据
	 */
	isBlob?: boolean;
}

interface HttpRequestConfig<D = AObject>
	extends AxiosRequestConfig<D>,
		HttpRequestConfigExtensions {}

interface HttpRequestResponse<T = AObject, D = AObject> extends AxiosResponse<T, D> {
	config: HttpRequestConfig<D> & { headers: AxiosRequestHeaders };
}

class HttpRequest {
	/**
	 * 请求实例
	 */
	service: AxiosInstance;
	/**
	 * 取消请求控制器Map
	 */
	abortControllerMap: Map<string, AbortController>;
	/**
	 * 消息提示实例
	 */
	messageInstance: Nullable<ReturnType<typeof AMessage.error>>;
	/**
	 * 登陆过期错误提示实例
	 */
	loginExpiredInstance: Nullable<ReturnType<typeof AMessage.error>>;
	/**
	 * 消息隐藏定时器ID，用于避免多个定时器冲突
	 */
	messageHideTimeoutId: Nullable<ReturnType<typeof setTimeout>>;
	/**
	 * 默认配置
	 */
	defaultConfig: AxiosRequestConfig = {
		baseURL: this.getBaseUrl(),
		timeout: +import.meta.env.VITE_API_TIMEOUT || 1000 * 30,
		headers: {
			"Content-Type": "application/json;charset=UTF-8",
		},
	};

	constructor(config?: AxiosRequestConfig) {
		// 实例化axios
		this.service = axios.create({ ...this.defaultConfig, ...config });
		this.abortControllerMap = new Map();
		this.messageHideTimeoutId = null;

		/**
		 * 请求拦截器
		 * token校验：存在时添加到请求头中
		 */
		this.service.interceptors.request.use(
			(config: HttpRequestConfig) => {
				const token = localStorage.getItem("token") || "";
				const lang = localStorage.getItem("lang") || "zh";

				const controller = new AbortController();
				const url = config.url || "";

				config.signal = controller.signal;
				this.abortControllerMap.set(url, controller);

				const headers = {
					Authorization: token,
					Token: token,
					"X-Requested-With": "XMLHttpRequest",
					"X-Lt-Language": lang,
					...config.headers,
				} as unknown as AxiosHeaders;

				if (config?.isBlob) {
					headers["responseType"] = "application/octet-stream";
				}

				return {
					...config,
					headers,
				};
			},
			(error: AxiosError) => {
				return Promise.reject(error);
			}
		);

		/**
		 * 响应拦截器
		 * 服务器换返回信息 -> [拦截统一处理]
		 */
		this.service.interceptors.response.use(
			(response: HttpRequestResponse<HttpResponse>) => {
				const { data, config, status } = response;
				const { t } = i18n.global;

				const url = config.url || "";

				// 清理已完成请求的AbortController
				this.clearAbortController(url);

				// 返回状态处理
				if ((status >= 200 && status < 300) || status === 304) {
					const code = data.code;

					if (code === window.$config.successCode) {
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						return Promise.resolve(data as any);
					}

					if (code === 999 && !this.loginExpiredInstance) {
						this.loginExpiredInstance = AMessage.error(t("loginExpired"), 0);

						// 取消剩下的请求
						this.cancelAllRequest();

						// 清空本地的token, 并重定向到登录页
						localStorage.clear();
						sessionStorage.clear();
						resetStores();

						// 在 1.5 秒后关闭登陆过期提示并清空实例
						setTimeout(() => {
							this.hideLoginExpiredMessage();
						}, 1500);

						// 获取当前应用类型
						const applicationType = window.__APP__ || "main";

						if (applicationType === "main") {
							mainStoreReset();
							router.replace("/login");
						}
					}

					if (config.showMessage && !this.loginExpiredInstance) {
						// 先关闭之前的消息，避免多条消息同时显示
						this.hideMessage();
						// 显示新的错误消息
						this.messageInstance = AMessage.error(data.msg || t("parameterError"), 0);
						// 设置消息隐藏定时器，确保消息在 1.5 秒后自动关闭
						this.scheduleMessageHide();
					}

					return Promise.reject(response);
				} else {
					return Promise.reject(response);
				}
			},
			(error: AxiosError<HttpResponse>) => {
				const { response, message: errorMessage } = error;
				const { t } = i18n.global;
				const status = response?.status;
				let message = "";

				if (response) {
					switch (status) {
						case 404:
							message = t("requestedNotFound");
							break;
						case 500:
							message = t("serverError");
							break;
						case 999:
							if (!this.loginExpiredInstance) {
								this.loginExpiredInstance = AMessage.error(t("loginExpired"), 0);

								// 取消剩下的请求
								this.cancelAllRequest();

								// 清空本地的token, 并重定向到登录页
								localStorage.clear();
								sessionStorage.clear();
								resetStores();
								router.replace("/login");

								// 在 1.5 秒后关闭登陆过期提示并清空实例
								setTimeout(() => {
									this.hideLoginExpiredMessage();
								}, 1500);
							}

							break;
						default:
							message = errorMessage || t("networkError");
							break;
					}
				} else {
					message = errorMessage || t("networkError");
				}

				// 只在没有登录过期提示且有错误信息时显示
				if (!this.loginExpiredInstance && message) {
					// 先关闭之前的消息，避免多条消息同时显示
					this.hideMessage();
					// 显示新的错误消息
					this.messageInstance = AMessage.error(message, 0);
					// 设置消息隐藏定时器，确保消息在 1.5 秒后自动关闭
					this.scheduleMessageHide();
				}

				return Promise.reject(error);
			}
		);
	}

	getBaseUrl() {
		const env = import.meta.env;
		const baseURL = env.VITE_APP_BASE_API || "/api";

		return baseURL;
	}

	/**
	 * 隐藏当前显示的错误消息
	 *
	 * 功能：
	 * 1. 清除已设置的隐藏定时器，防止多个定时器冲突导致消息被重复调用
	 * 2. 立即调用消息实例来关闭消息显示
	 * 3. 清空消息实例引用，避免重复操作
	 */
	private hideMessage() {
		// 清除已设置的隐藏定时器，防止多个定时器冲突
		if (this.messageHideTimeoutId) {
			clearTimeout(this.messageHideTimeoutId);
			this.messageHideTimeoutId = null;
		}

		// 关闭现有的错误消息
		if (this.messageInstance) {
			this.messageInstance();
			this.messageInstance = null;
		}
	}

	/**
	 * 隐藏登陆过期提示
	 *
	 * 功能：
	 * 1. 立即调用登陆过期消息实例来关闭消息显示
	 * 2. 清空登陆过期消息实例引用，允许后续重新显示
	 */
	private hideLoginExpiredMessage() {
		if (this.loginExpiredInstance) {
			this.loginExpiredInstance();
			this.loginExpiredInstance = null;
		}
	}

	/**
	 * 设置消息隐藏的定时器
	 *
	 * 功能：
	 * 1. 清除之前的定时器，确保只有一个活跃的隐藏定时器
	 * 2. 在 1.5 秒后自动隐藏错误消息
	 * 3. 解决多个并发请求导致消息一直显示的问题
	 *
	 * 场景：
	 * - 当多个请求在短时间内都失败时，每个请求都会设置一个消息和定时器
	 * - 旧的方案中多个定时器会互相覆盖，导致某些消息无法被正确清理
	 * - 新方案确保只有最后一个消息的定时器会执行，避免消息残留
	 */
	private scheduleMessageHide() {
		// 清除之前的定时器，确保只有一个活跃的隐藏定时器
		if (this.messageHideTimeoutId) {
			clearTimeout(this.messageHideTimeoutId);
		}

		// 设置新的隐藏定时器
		this.messageHideTimeoutId = setTimeout(() => {
			this.hideMessage();
		}, 1500);
	}

	/**
	 * @description 常用方法封装
	 * T：返回类型定义
	 * P：参数类型定义
	 * url：请求地址
	 * params：请求参数
	 * config：请求配置（一般不用，特殊情况需要添加配置时使用）
	 */
	get<T = AObject, P = AObject>(
		url: string,
		params?: P,
		config?: HttpRequestConfig
	): Promise<HttpResponse<T>> {
		return this.service.get(url, { params, ...config });
	}

	post<T = AObject, P = AObject>(
		url: string,
		params?: P,
		config?: HttpRequestConfig
	): Promise<HttpResponse<T>> {
		return this.service.post(url, params, { showMessage: true, ...config } as AxiosRequestConfig);
	}

	put<T = AObject, P = AObject>(
		url: string,
		params?: P,
		config?: HttpRequestConfig
	): Promise<HttpResponse<T>> {
		return this.service.put(url, params, { showMessage: true, ...config } as AxiosRequestConfig);
	}

	delete<T = AObject, P = AObject>(
		url: string,
		params?: P,
		config?: HttpRequestConfig
	): Promise<HttpResponse<T>> {
		return this.service.delete(url, { params, ...config });
	}

	/**
	 * 取消全部请求
	 */
	cancelAllRequest() {
		for (const [, controller] of this.abortControllerMap) {
			controller.abort();
		}

		this.abortControllerMap.clear();
	}

	/**
	 * 取消指定的请求
	 * @param url 待取消的请求URL
	 */
	cancelRequest(url: string | string[]) {
		const urlList = Array.isArray(url) ? url : [url];

		for (const key of urlList) {
			this.abortControllerMap.get(key)?.abort();
			this.abortControllerMap.delete(key);
		}
	}

	/**
	 * 清理请求完成的AbortController
	 * @param url 请求URL
	 */
	clearAbortController(url: string) {
		if (this.abortControllerMap.has(url)) {
			this.abortControllerMap.delete(url);
		}
	}
}

export default new HttpRequest();
